import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Palette, ImagePlus, Upload, Sparkles, Lock, Crown } from 'lucide-react';
import ImageGallery from '../../components/image-studio/ImageGallery';
import ImageGenerator from '../../components/image-studio/ImageGenerator';
import { imageStudioService } from '../../services/imageStudioService';
import { GeneratedImage, ImageUsageStats } from '../../types/imageStudio';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import UsageBadge from '../../components/ui/UsageBadge';

const ImageStudioPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, plan } = useAuth();
  const [activeTab, setActiveTab] = useState<'gallery' | 'create'>('gallery');
  const [stats, setStats] = useState<ImageUsageStats | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialReferenceUrl, setInitialReferenceUrl] = useState<string | null>(null);

  // Check if feature is enabled via plan limits
  const planLimits = plan?.limits;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isEnterprise = plan?.slug === 'enterprise';
  // Gate por plano: habilitado se o plano tem image_studio_enabled (planos
  // antigos sem a flag continuam liberados; flag explicitamente false bloqueia)
  const isEnabled = isAdmin || isEnterprise || (planLimits ? planLimits.image_studio_enabled !== false : false);
  const generationLimit = isEnterprise ? -1 : (planLimits?.image_generations ?? planLimits?.image_generations_limit ?? 5);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await imageStudioService.getUsageStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch {
      // Silent fail
    }
  };

  const handleImageGenerated = (image: GeneratedImage) => {
    setRefreshTrigger(prev => prev + 1);
    loadStats();
    setActiveTab('gallery');
  };

  const handleEdit = (image: GeneratedImage) => {
    navigate(`/image-studio/editor/${image.id}`);
  };

  const handleUseAsReference = (image: GeneratedImage) => {
    const url = image.edited_url || image.original_url || image.thumbnail_url || null;
    if (!url) {
      toast.error(t('imageStudio.useAsReferenceError', 'Could not get image URL'));
      return;
    }
    setInitialReferenceUrl(url);
    setActiveTab('create');
    toast.success(t('imageStudio.useAsReferenceLoaded', 'Imagem carregada como referência'));
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('imageStudio.invalidFile', 'Please select an image file'));
      e.target.value = '';
      return;
    }
    // O backend (multer) rejeita acima de 10MB — avisa antes de subir à toa
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('imageStudio.reference.tooBig', 'Image must be under 10 MB.'));
      e.target.value = '';
      return;
    }

    try {
      const response = await imageStudioService.uploadImage(file, { title: file.name });
      if (response.success) {
        toast.success(t('imageStudio.uploaded', 'Image uploaded!'));
        setRefreshTrigger(prev => prev + 1);
        loadStats();
      }
    } catch {
      toast.error(t('imageStudio.uploadError', 'Upload failed'));
    }

    e.target.value = '';
  };

  // Upgrade screen if not enabled
  if (!isEnabled) {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-primary-600 dark:text-primary-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {t('imageStudio.upgradeTitle', 'Image Studio')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
          {t('imageStudio.upgradeDescription', 'Generate and edit professional images with AI. Upgrade your plan to access Image Studio.')}
        </p>
        <button
          onClick={() => navigate('/subscription')}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-purple-600 text-white font-medium hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg"
        >
          <Crown className="w-5 h-5" />
          {t('imageStudio.upgradePlan', 'Upgrade Plan')}
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'gallery' as const, label: t('imageStudio.gallery', 'Gallery'), icon: Palette },
    { id: 'create' as const, label: t('imageStudio.create', 'Create'), icon: Sparkles },
  ];

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block p-2 rounded-xl bg-gradient-to-br from-primary-500/10 to-purple-500/10">
              <Palette className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            {t('imageStudio.title', 'Image Studio')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1">
            {t('imageStudio.subtitle', 'Create, edit and manage your images with AI')}
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <UsageBadge resource="image_generations" />

          {/* Upload Button (celular: só o ícone) */}
          <label
            aria-label={t('imageStudio.upload', 'Upload')}
            title={t('imageStudio.upload', 'Upload')}
            className="ml-auto sm:ml-0 flex-shrink-0 h-10 inline-flex items-center justify-center gap-2 px-3 sm:px-4 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">{t('imageStudio.upload', 'Upload')}</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </label>

          {/* New Image Button */}
          <button
            onClick={() => setActiveTab('create')}
            className="flex-shrink-0 h-10 whitespace-nowrap inline-flex items-center justify-center gap-2 px-3 sm:px-4 rounded-xl bg-gradient-to-r from-primary-600 to-purple-600 text-white text-sm font-medium hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg shadow-primary-600/25"
          >
            <ImagePlus className="w-4 h-4" />
            <span className="sm:hidden">{t('imageStudio.newImageShort', 'Nova')}</span>
            <span className="hidden sm:inline">{t('imageStudio.newImage', 'New Image')}</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full sm:w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'gallery' && (
        <ImageGallery
          onEdit={handleEdit}
          onUseAsReference={handleUseAsReference}
          refreshTrigger={refreshTrigger}
          onCreate={() => setActiveTab('create')}
        />
      )}

      {activeTab === 'create' && (
        <div className="max-w-2xl">
          <ImageGenerator
            onImageGenerated={handleImageGenerated}
            initialReferenceUrl={initialReferenceUrl}
            onReferenceConsumed={() => setInitialReferenceUrl(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ImageStudioPage;
